import Page from "components/page/Page";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Card from "components/card/Card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faPalette } from "@fortawesome/pro-regular-svg-icons";
import { Badge, TBadgeType } from "components/badge/Badge";
import { faCircle } from "@fortawesome/pro-solid-svg-icons";
import Input from "components/form/input";
import { Select } from "components/select/index";
import "./websiteBuilderSettings.scss";
import { useWebsite } from "../shared/hooks/useWebsite";
import { IWebsiteBuilderWebsiteVersion } from "redux/reducers/models/websiteBuilder";
import { useHistory } from "react-router";
import { WebsiteBuilderLoadingContent } from "./components/loadingContent/WebsiteBuilderLoadingContent";
import { useWebsiteBuilderLoadingState } from "../shared/hooks/useWebsiteBuilderLoadingState";
import axios, { CancelToken } from "axios";
import { StatusCode } from "api/protocols";
import { GetWebsiteBuilderWebsiteVersions } from "api/rpc/2024-04/facilityAdmin/websiteBuilder/websiteBuilder";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { useTranslation } from "react-i18next";

type TWebsiteVersionStatusFilter = "all" | "published" | "draft";

const versionStatusFilters: TWebsiteVersionStatusFilter[] = ["all", "published", "draft"];

export default function WebsiteBuilderSettings() {
  const { t } = useTranslation();
  
  const dispatch = useAppDispatch();
  
  const history = useHistory();

  const { Option } = Select;

  // const { state: customDomainModal, updateModal: updateCustomDomainModal, closeModal: closeCustomDomainModal } = useModal();

  const {
    website,
    websiteExists,
    websiteLoadingStatus,
  } = useWebsite({ websiteMustExist: true });

  const {
    state: versions,
    stateExists: versionsExist,
    stateLoadingStatus: versionsLoadingStatus,
    loadInProgress: versionsLoadInProgress,
    loadFailed: versionsLoadFailed,
    loadSucceeded: versionsLoadSucceeded,
  } = useWebsiteBuilderLoadingState<IWebsiteBuilderWebsiteVersion[]>({
    state: null,
    stateExists: false,
    stateLoadingStatus: "loading",
  });

  const [versionStatusFilter, setVersionStatusFilter] = useState<TWebsiteVersionStatusFilter>("all");

  const currentVersionId = website?.current_version_id;

  const filteredVersions: IWebsiteBuilderWebsiteVersion[] = useMemo(() => {
    if (versions?.length > 0) {
      return versions.filter(
        version =>
          versionStatusFilter === "all" ||
          (versionStatusFilter === "draft" && version.published_at == null) ||
          (versionStatusFilter === "published" && version.published_at != null),
      );
    } else {
      return [];
    }
  }, [versions, versionStatusFilter]);

  const loadVersions = useCallback(async (token: CancelToken) => {
    if (website?.id != null) {
      versionsLoadInProgress();

      const res = await GetWebsiteBuilderWebsiteVersions({ website_id: website.id }, false, token);

      if (res.status !== StatusCode.OK) {
        if (!token.reason) {
          versionsLoadFailed();
          dispatch(showError(typeof res.data === "string" ? res.data : t("Error loading versions")));
        }

        return;
      }

      versionsLoadSucceeded(res.data ?? [], true);
    }
  }, [website?.id]);

  useEffect(() => {
    const source = axios.CancelToken.source();

    void loadVersions(source.token);

    return () => source.cancel();
  }, [loadVersions]);

  function renderDomainActiveBadge(active: boolean) {
    let statusType: TBadgeType = "gray";
    let statusText = "";

    if (active) {
      statusType = "success";
      statusText = t("Active");
    } else {
      statusType = "gray";
      statusText = t("Inactive");
    }

    return (
      <Badge iconLeft={<FontAwesomeIcon className="settings-domain-active-icon" icon={faCircle} />} type={statusType}>
        {statusText}
      </Badge>
    );
  }

  function getReadableDateFromTimestamp(timestamp: string) {    
    const date = new Date(timestamp);
    const readableDate =
      date.toLocaleString("en-us", { month: "long", day: "numeric", year: "numeric" })
      + " " + t("at") + " " +
      date.toLocaleString("en-us", { timeStyle: "short" });

    return readableDate;
  }

  function navigateToEditor(pastWebsiteVersionId?: string) {
    history.push(`/admin/website-builder-editor${pastWebsiteVersionId ? `/${pastWebsiteVersionId}` : ""}`);
  }

  function renderTeeOnDomain() {
    return (
      <Card>
        <Card.Section>
          <div className="settings-domain">
            <Input
              label={t("Domain")}
              className="settings-domain-input"
              placeholder={`${t("yourwebsite")}`}
              value={website.domain}
              readOnly
            />
            {renderDomainActiveBadge(website.domain != null)}
          </div>
        </Card.Section>
      </Card>
    );
  }

  // function renderCustomDomain() {
  //   return (
  //     <Card>
  //       <Card.Section>
  //         <div className="settings-domain">
  //           <Input
  //             label={t("Custom Domain")}
  //             className="settings-domain-input"
  //             placeholder={`${t("yourwebsite")}.com`}
  //             suffix={
  //               <div className="settings-domain-input-connect">
  //                 <FontAwesomeIcon icon={faLink} />
  //                 <div>{t("Connect")}</div>
  //               </div>
  //             }
  //             readOnly
  //             value=""
  //           />
  //           {renderDomainActiveBadge(false)}
  //         </div>
  //       </Card.Section>
  //     </Card>
  //   );
  // }
  
  function renderSettings() {
    if (
      websiteLoadingStatus === "succeeded" &&
      websiteExists &&
      versionsLoadingStatus === "succeeded" &&
      versionsExist
    ) {
      return (
        <div id="website-builder-settings">
          <Card
            // title={t("Connect URL")}
            // subtitle={t("Choose a free TeeOn URL or connect your own custom domain for site visitors to access your website.")}
            title="Domain"
            subtitle="View your website's domain"
          >
            <Card.Section>
              <div className="settings-domains-container">
                {renderTeeOnDomain()}
                {/* {renderCustomDomain()} */}
              </div>
            </Card.Section>
          </Card>
          <Card
            title={t("Version history")}
            subtitle={t("Manage the versions of your website")}
          >
            <Card.Section>
              {versions?.length > 0 && (
                <>
                  <div className="settings-version-filter-container">
                    <Select defaultValue={versionStatusFilter} onChange={setVersionStatusFilter}>
                      {versionStatusFilters.map((filter, index) => {
                        let statusText = "";

                        if (filter === "all") {
                          statusText = t("All versions");
                        } else if (filter === "published") {
                          statusText = t("Published version");
                        } else if (filter === "draft") {
                          statusText = t("Draft versions");
                        }

                        return (
                          <Option key={index} value={filter}>
                            {statusText}
                          </Option>
                        );
                      })}
                    </Select>
                  </div>
                  {filteredVersions.length > 0 && (
                    <div className="settings-versions">
                      {filteredVersions.map(version => {
                        const isCurrentVersion = version.id === currentVersionId;

                        let statusType: TBadgeType = "gray";
                        let statusText = "";

                        if (version.published_at != null) {
                          statusType = "success";
                          statusText = t("Published");
                        } else {
                          statusType = "gray";
                          statusText = t("Draft");
                        }

                        return (
                          <Card key={version.id}>
                            <Card.Section>
                              <div className="settings-version">
                                <div>
                                  <div className="settings-version-date">
                                    {getReadableDateFromTimestamp(version.updated_at)}
                                  </div>
                                  <div className="settings-version-author">
                                    {t("Last modified by")} {version.edited_by_user_full_name}
                                  </div>
                                </div>
                                <div className="settings-version-actions">
                                  <Badge type={statusType}>{statusText}</Badge>
                                  <FontAwesomeIcon
                                    className="settings-version-preview-icon"
                                    icon={faEye}
                                    onClick={() => navigateToEditor(!isCurrentVersion ? String(version.id) : null)}
                                  />
                                </div>
                              </div>
                            </Card.Section>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </Card.Section>
          </Card>
        </div>
      );
    } else {
      return null;
    }
  }

  return (
    <Page
      narrow
      title={t("Settings")}
      subtitle={t("Manage your website settings")}
      primaryAction={{
        content:
          <>
            <FontAwesomeIcon icon={faPalette} className="mr-2" />{t("Customize website")}
          </>,
        action: () => navigateToEditor(),
        disabled: websiteLoadingStatus !== "succeeded",
      }}
    >
      <WebsiteBuilderLoadingContent loading={websiteLoadingStatus === "standby" || websiteLoadingStatus === "loading" || versionsLoadingStatus === "loading"}>
        {renderSettings()}
      </WebsiteBuilderLoadingContent>
      {/* <Portal isMounted={customDomainModal.isOpen}>
        <Sheet
          title={t("Link your custom domain")}
          supportText={t("Review the instructions below to connect your custom domain.")}
          open={customDomainModal.isOpen}
          size="small"
          closable
          onCancel={closeCustomDomainModal}
          onOk={closeCustomDomainModal}
          okText={t("Confirm")}
          cancelText={t("Close")}
        >
          <div className="website-builder-custom-domain-instructions">
            <div className="custom-domain-instruction">
              <div className="custom-domain-instruction-number">1</div>
              <div>{t("Login to your domain provider account.")}</div>
            </div>
            <div className="custom-domain-instruction">
              <div className="custom-domain-instruction-number">2</div>
              <div>{t("Locate DNS record settings of your custom domain under domain management.")}</div>
            </div>
            <div className="custom-domain-instruction">
              <div className="custom-domain-instruction-number">3</div>
              <div>{t("Add or edit your A records.")}</div>
            </div>
          </div>
        </Sheet>
      </Portal> */}
    </Page>
  );
}
